import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Badge, Button, Drawer, Group, Loader, Paper, Select, Stack,
  Switch, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconSitemap, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useCostCenters, useCreateCostCenter, useDeleteCostCenter, useUpdateCostCenter,
  type CostCenterDto,
} from './costCentersApi';

export function CostCentersPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useCostCenters();
  const create = useCreateCostCenter();
  const update = useUpdateCostCenter();
  const del = useDeleteCostCenter();

  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState<CostCenterDto | null>(null);
  const [deleting, setDeleting] = useState<CostCenterDto | null>(null);

  const rows = data ?? [];
  const nameOf = (c: CostCenterDto) => (i18n.language === 'ar' ? c.nameAr : (c.nameEn ?? c.nameAr));
  const byId = useMemo(() => new Map(rows.map((c) => [c.costCenterId, c])), [rows]);

  const form = useForm({
    initialValues: { code: '', nameAr: '', nameEn: '', parentCostCenterId: null as string | null, isGroup: false, isActive: true },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ code: '', nameAr: '', nameEn: '', parentCostCenterId: null, isGroup: false, isActive: true });
    form.resetDirty();
    setDrawer(true);
  };
  const openEdit = (c: CostCenterDto) => {
    setEditing(c);
    form.setValues({ code: c.code, nameAr: c.nameAr, nameEn: c.nameEn ?? '', parentCostCenterId: c.parentCostCenterId, isGroup: c.isGroup, isActive: c.isActive });
    form.resetDirty();
    setDrawer(true);
  };

  const submit = form.onSubmit(async (v) => {
    const req = { code: v.code.trim(), nameAr: v.nameAr.trim(), nameEn: v.nameEn.trim() || null, parentCostCenterId: v.parentCostCenterId, isGroup: v.isGroup, isActive: v.isActive };
    try {
      if (editing) await update.mutateAsync({ id: editing.costCenterId, req });
      else await create.mutateAsync(req);
      notifications.show({ color: 'green', title: t('common.toasts.saved'), message: v.nameAr });
      setDrawer(false);
    } catch (err) {
      notifications.show({ color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody') });
    }
  });

  // Parent options: group nodes only, excluding self.
  const parentOptions = rows
    .filter((c) => c.isGroup && c.costCenterId !== editing?.costCenterId)
    .map((c) => ({ value: c.costCenterId, label: `${c.code} — ${nameOf(c)}` }));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.costCenters.title')}
        subtitle={t('accounting.costCenters.subtitle')}
        actions={<Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>{t('accounting.costCenters.create')}</Button>}
      />
      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconSitemap size={22} stroke={1.6} />} title={t('accounting.costCenters.empty')}
            description={t('accounting.costCenters.emptyBody')} actionLabel={t('accounting.costCenters.create')} onAction={openCreate} />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.costCenters.code')}</Table.Th>
                <Table.Th>{t('accounting.costCenters.name')}</Table.Th>
                <Table.Th>{t('accounting.costCenters.parent')}</Table.Th>
                <Table.Th>{t('accounting.costCenters.type')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((c) => (
                <Table.Tr key={c.costCenterId}>
                  <Table.Td><span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">{c.code}</span></Table.Td>
                  <Table.Td><Text fw={500} c="var(--wheels-ink-strong)">{nameOf(c)}</Text></Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{c.parentCostCenterId ? (byId.get(c.parentCostCenterId) ? nameOf(byId.get(c.parentCostCenterId)!) : '—') : '—'}</Table.Td>
                  <Table.Td>
                    {c.isGroup
                      ? <Badge color="blue" variant="light" radius="sm">{t('accounting.costCenters.group')}</Badge>
                      : <Badge color="gray" variant="light" radius="sm">{t('accounting.costCenters.leaf')}</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(c)} aria-label={t('common.edit')}><IconEdit size={16} stroke={1.6} /></ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon variant="subtle" color="red" onClick={() => setDeleting(c)} aria-label={t('common.delete')}><IconTrash size={16} stroke={1.6} /></ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer opened={drawer} onClose={() => setDrawer(false)} position={i18n.language === 'ar' ? 'left' : 'right'}
        size="md" padding="lg" title={editing ? t('accounting.costCenters.editTitle') : t('accounting.costCenters.create')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <TextInput label={t('accounting.costCenters.code')} withAsterisk className="font-numeric" {...form.getInputProps('code')} />
            <TextInput label={t('accounting.costCenters.nameAr')} withAsterisk {...form.getInputProps('nameAr')} />
            <TextInput label={t('accounting.costCenters.nameEn')} {...form.getInputProps('nameEn')} />
            <Select label={t('accounting.costCenters.parent')} data={parentOptions} clearable searchable
              placeholder={t('accounting.costCenters.noParent')} {...form.getInputProps('parentCostCenterId')} />
            <Switch label={t('accounting.costCenters.isGroup')} description={t('accounting.costCenters.isGroupHelp')}
              checked={form.values.isGroup} onChange={(e) => form.setFieldValue('isGroup', e.currentTarget.checked)} />
            {editing && (
              <Switch label={t('common.active')} checked={form.values.isActive}
                onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)} />
            )}
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={create.isPending || update.isPending}>{editing ? t('common.save') : t('common.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      <ConfirmDialog opened={deleting !== null} onClose={() => setDeleting(null)}
        onConfirm={async () => { if (!deleting) return; try { await del.mutateAsync(deleting.costCenterId); setDeleting(null); } catch (err) { notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody') }); } }}
        title={t('accounting.costCenters.deleteTitle')} message={t('accounting.costCenters.deleteBody', { name: deleting ? nameOf(deleting) : '' })}
        loading={del.isPending} />
    </Stack>
  );
}
